'use client'

import {useEffect, useMemo, useRef, useState} from 'react'
import {Play, ShieldAlert} from 'lucide-react'
import type {Account, ApiResponse, Contact} from '@/types'
import {apiPost} from '@/lib/api'
import {avatarDisplaySrc, displayName, formatFriendRelation} from '@/lib/utils'
import {PageHeader} from '@/components/PageHeader'
import {ContactPicker} from '@/components/ContactPicker'

type SmartFriendStatusResponse = {
    success: boolean
    response?: ApiResponse
    /** 0 normal · 1 deleted-you · 2 blocked-you · 3 you-blocked · 9 detect-failed */
    level?: number
    label?: string
    detail?: string
    signals?: Record<string, unknown>
    message?: string
}

export function ContactStatusView({
    contacts,
    account,
}: {
    contacts: Contact[]
    account: Account | null
}) {
    const [selectedWxid, setSelectedWxid] = useState('')
    const [scanning, setScanning] = useState(false)
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState('')
    const [resultLevel, setResultLevel] = useState<number>(0)
    const [resultDetail, setResultDetail] = useState<string>('')
    const [error, setError] = useState('')
    const [apiResponse, setApiResponse] = useState<ApiResponse | undefined>(undefined)
    const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null)

    const selectedContact = useMemo(
        () => contacts.find((contact) => contact.wxid === selectedWxid) || null,
        [contacts, selectedWxid],
    )

    useEffect(() => {
        return () => {
            if (progressTimer.current) {
                clearInterval(progressTimer.current)
                progressTimer.current = null
            }
        }
    }, [])

    const stopProgress = () => {
        if (progressTimer.current) {
            clearInterval(progressTimer.current)
            progressTimer.current = null
        }
    }

    const startScan = () => {
        if (!account || !selectedWxid || scanning) return
        setScanning(true)
        setProgress(0)
        setError('')
        stopProgress()
        progressTimer.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    stopProgress()
                    setScanning(false)
                    return 100
                }
                return prev + 10
            })
        }, 150)

        apiPost<SmartFriendStatusResponse>('/friend-status', {
            wxid: account.wxid,
            userName: selectedWxid,
        })
            .then((response) => {
                if (response.success) {
                    setApiResponse(response.response)
                    // Smart endpoint already gave us the verdict — prefer it.
                    if (typeof response.level === 'number' && response.label) {
                        setResultLevel(response.level)
                        setResult(response.label)
                        setResultDetail(response.detail || '')
                    } else {
                        // Legacy endpoint fallback (older Go binary still in prod).
                        setResultLevel(0)
                        setResult(formatFriendRelation(response.response))
                        setResultDetail('')
                    }
                } else {
                    setError(response.message || '检测失败')
                }
            })
            .catch((err) => {
                setError(err instanceof Error ? err.message : '检测失败')
            })
            .finally(() => {
                stopProgress()
                setProgress(100)
                setScanning(false)
            })
    }

    const canShowSearch = !scanning && progress === 0
    const heading = scanning
        ? `正在检测 ${selectedContact ? displayName(selectedContact) : '...'}...`
        : progress === 100
            ? '检测完成'
            : '等待检测'

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="单人关系检测"
                description='无痕检测对方是否已删除你；通过双协议交叉验证提升准确率，但无法 100% 识别"对方拉黑你"'
            />
            <div className="flex-1 overflow-y-auto p-3 md:p-6 flex flex-col items-center">
                <div className="w-full max-w-2xl bg-white rounded-card shadow-card border border-hair p-10 flex flex-col items-center mt-10">
                    {canShowSearch ? (
                        <div className="w-full max-w-md mb-8">
                            <label className="block text-sm font-medium text-soft mb-2 text-center">
                                搜索并选择要检测的好友
                            </label>
                            <ContactPicker
                                contacts={contacts}
                                value={selectedWxid}
                                onChange={(wxid) => {
                                    setSelectedWxid(wxid)
                                    setResult('')
                                    setResultLevel(0)
                                    setResultDetail('')
                                    setError('')
                                    setProgress(0)
                                    setApiResponse(undefined)
                                }}
                                dropdownClassName="max-h-48"
                            />
                        </div>
                    ) : null}

                    <div
                        className={`w-32 h-32 rounded-full border-4 border-hair flex items-center justify-center mb-6 relative ${
                            !scanning && selectedContact ? 'cursor-pointer hover:border-accent/50 transition-colors' : ''
                        }`}
                        onClick={() => {
                            if (scanning || !selectedContact) return
                            // Click avatar to re-select a different contact
                            setSelectedWxid('')
                            setResult('')
                            setResultLevel(0)
                            setResultDetail('')
                            setError('')
                            setProgress(0)
                            setApiResponse(undefined)
                        }}
                        title={!scanning && selectedContact ? '点击选择其他好友' : undefined}
                    >
                        {scanning ? (
                            <div className="absolute inset-0 border-4 border-accent rounded-full border-t-transparent animate-spin"/>
                        ) : null}
                        {selectedContact ? (
                            <img
                                src={avatarDisplaySrc(selectedContact.avatar, selectedContact.wxid)}
                                alt={displayName(selectedContact)}
                                className="w-24 h-24 rounded-full bg-gray-100"
                            />
                        ) : (
                            <ShieldAlert size={48} className="text-muted"/>
                        )}
                    </div>

                    <div className="w-full max-w-sm mb-8">
                        <h3 className="text-lg font-medium text-ink mb-2 text-center">{heading}</h3>
                        {scanning || progress === 100 ? (
                            <div className="w-full bg-hair rounded-full h-2.5 mb-2 overflow-hidden">
                                <div
                                    className="bg-accent h-2.5 rounded-full transition-all duration-300"
                                    style={{width: `${progress}%`}}
                                />
                            </div>
                        ) : null}
                    </div>

                    {!scanning ? (
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={!selectedWxid}
                            onClick={startScan}
                        >
                            <Play size={16}/>
                            {progress === 100 ? '重新检测' : '开始检测'}
                        </button>
                    ) : null}
                </div>

                {progress === 100 && selectedContact && result && !error ? (() => {
                    // Map level → palette so the card visually matches severity.
                    const palette = (() => {
                        if (resultLevel === 0) {
                            return {
                                wrap: 'bg-emerald-50 text-emerald-700 border-emerald-100',
                                badge: 'bg-emerald-200',
                            }
                        }
                        if (resultLevel === 9) {
                            return {
                                wrap: 'bg-gray-50 text-gray-700 border-gray-200',
                                badge: 'bg-gray-200',
                            }
                        }
                        return {
                            wrap: 'bg-orange-50 text-orange-700 border-orange-100',
                            badge: 'bg-orange-200',
                        }
                    })()
                    return (
                        <div className="w-full max-w-2xl mt-6 bg-white rounded-card shadow-card border border-hair overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b border-hair text-sm font-medium text-soft">
                                检测结果
                            </div>
                            <div className="p-6 space-y-3">
                                <div className={`flex items-center justify-between p-4 rounded border ${palette.wrap}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <ShieldAlert size={24} className="shrink-0"/>
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{displayName(selectedContact)}</div>
                                            <div className="text-xs opacity-80 truncate">{selectedContact.wxid}</div>
                                        </div>
                                    </div>
                                    <span className={`text-sm px-3 py-1.5 rounded font-medium shrink-0 ${palette.badge}`}>
                                        {result}
                                    </span>
                                </div>
                                {/*{resultDetail ? (*/}
                                {/*    <p className="text-xs text-muted">*/}
                                {/*        线索：{resultDetail}*/}
                                {/*    </p>*/}
                                {/*) : null}*/}
                                {/*{resultLevel === 0 && apiResponse ? (*/}
                                {/*    <p className="text-xs text-muted">*/}
                                {/*        当前两条协议都没有上报异常，但微信不会把"对方拉黑你"在该路径回传，*/}
                                {/*        所以"未检测到异常"不等于"一定正常"。*/}
                                {/*    </p>*/}
                                {/*) : null}*/}
                            </div>
                        </div>
                    )
                })() : null}

                {error ? (
                    <div className="w-full max-w-2xl mt-6 bg-white rounded-card shadow-card border border-hair overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-hair text-sm font-medium text-soft">
                            检测失败
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded border border-red-100">
                                <ShieldAlert size={24} className="shrink-0"/>
                                <span className="text-sm">{error}</span>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    )
}
